import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
  Collapse,
} from '@mui/material';
import { useForm, Controller, SubmitHandler, SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNotify, useDataProvider } from 'react-admin';
import { useMutation, useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { useCosmosQueryClient } from 'providers/clients/CosmosQueryClientProvider';
import { useIntersection } from '@mantine/hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import DrawerWrapper from 'components/DrawerWrapper/DrawerWrapper';
import { DragModal } from 'components/DragModal/DragModal';
import { BetaFlag } from 'components/generic/BetaFlag';
import { TWidget } from 'utility/types/dataProvider';
import { useMemo } from 'react';
import { DynamicFieldWrapper } from 'components/DynamicFormField/components/DynamicFieldWrapper';
import type { DynamicFieldType } from 'components/DynamicFormField/DynamicFormField';
import { getValidationRules } from 'components/DynamicFormField/utility/functions';
import useWidgetAlerts from '../hooks/useWidgetAlerts';
import useAlertCriteriaWatch from '../hooks/useAlertCriteriaWatch';
import { useDashboardContext } from 'screens/dashboard/Dashboard';
import type {
  TPostWidgetAlertPayload,
  TPutWidgetAlertByIdPayload,
  TWidgetAlert,
} from 'utility/types/dashboard';
import type { DashboardsProviderType } from 'providers/contexts/extensions/Dashboards';
import WidgetAlertsList from './WidgetAlertsList';
import type { TDeleteWidgetAlertByIdParams } from 'utility/types/dashboard';

type TWidgetAlertsProps = {
  open: boolean;
  onClose: () => void;
  widget: TWidget;
};

type TAlertView = 'list' | 'form';

// Field grouping configuration for better UX
type FieldGroup = {
  key: string;
  title: string;
  description?: string;
  fieldNames: string[];
};

/**
 * Strip the `criteria.` namespace prefix that some API field definitions use,
 * so that all form values are stored at the top level of the RHF store (flat).
 * e.g. "criteria.operator" → "operator"
 */
const flatFieldName = (name: string) =>
  name.startsWith('criteria.') ? name.slice('criteria.'.length) : name;

/**
 * Wrapper around getValidationRules that always treats every alert field as required,
 * regardless of what the API field definition says.
 */
const getAlertValidationRules: typeof getValidationRules = (args) =>
  getValidationRules({ ...args, field: { ...args.field, required: true } });

const FIELD_GROUPS: FieldGroup[] = [
  {
    key: 'basic',
    title: 'Alert Details',
    fieldNames: ['title', 'redirect_url', 'domains'],
  },
  {
    key: 'criteria',
    title: 'Alert Criteria',
    description: 'Define when this alert should trigger',
    fieldNames: [
      // Basic criteria fields
      'compare_type',
      'data_column',
      'criteria.data_column',
      'operator',
      'criteria.operator',
      // Threshold reference - controls visibility of threshold_column and threshold_column_value
      'threshold_reference',
      'criteria.threshold_reference',
      // Threshold group field - contains children (threshold_text, threshold_int, threshold_float).
      // The active child is selected at render time based on compare_type; the group field
      // itself is never rendered directly.
      'threshold',
      // Conditional sub-fields - shown based on threshold_reference value
      // threshold_column: shown when threshold_reference is 'individual_specified'
      'threshold_column',
      'criteria.threshold_column',
      // threshold_column_value: shown when threshold_column has a value
      'threshold_column_value',
      'criteria.threshold_column_value',
      // Alert expiration
      'alert_expiration',
      'criteria.alert_expiration',
      // Alternative field name used by some alert types
      'column',
      // Date range
      'date_range',
      // Trending alert filter fields
      'content_group',
      'author_email',
      'content_type',
      'traffic_source',
    ],
  },
  {
    key: 'schedule',
    title: 'Schedule',
    description: 'When should this alert run',
    fieldNames: ['cadence', 'cadence_time'],
  },
  {
    key: 'notification',
    title: 'Notification Settings',
    fieldNames: ['parameters'],
  },
];

const WidgetAlerts = ({ open, onClose, widget }: TWidgetAlertsProps) => {
  const [view, setView] = useState<TAlertView>('list');
  const [selectedAlert, setSelectedAlert] = useState<TWidgetAlert | null>(null);
  const { t } = useTranslation();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { dashboard } = useDashboardContext();
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set());
  const [deletingAlertId, setDeletingAlertId] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const { queryClient } = useCosmosQueryClient();

  // Intersection observer for infinite scroll
  const { ref: intersectionRef, entry } = useIntersection({ threshold: 1 });

  // Fetch all alerts for this widget with infinite query.
  const {
    data: widgetAlertsResponse,
    isLoading: isLoadingWidgetAlerts,
    isFetching: isFetchingWidgetAlerts,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['getWidgetAlerts', 'widget-alerts-list', widget?.id, dashboard?.id],
    queryFn: async ({ pageParam = '' }) => {
      if (!widget?.id) {
        return { items: [], total: 0, page: 0, limit: 25, next: null, prev: null };
      }

      try {
        const extendedDataProvider = dataProvider as typeof dataProvider &
          DashboardsProviderType;

        const response = await extendedDataProvider.getWidgetAlerts([widget.id], {
          page: String(pageParam),
          limit: 25,
        });

        return response;
      } catch (error) {
        console.error('[WidgetAlerts] Error fetching widget alerts:', error);
        return { items: [], total: 0, page: 0, limit: 25, next: null, prev: null };
      }
    },
    enabled: Boolean(widget?.id) && open,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    placeholderData: (previousData) => previousData,
    initialPageParam: '',
    getNextPageParam: (lastPage) => {
      if (!lastPage) return undefined;
      return lastPage.next || undefined;
    },
    staleTime: 30000,
  });

  // Trigger fetching more data when the observer intersects
  useEffect(() => {
    if (entry?.isIntersecting && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [entry?.isIntersecting, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Extract and flatten alerts from all pages
  const widgetAlerts = useMemo(() => {
    const infiniteData = widgetAlertsResponse as
      | InfiniteData<{
          items: TWidgetAlert[];
          total: number;
          page: number;
          limit: number;
        }>
      | undefined;

    if (!infiniteData?.pages) return [];

    // Flatten all pages into a single array
    const allAlerts = infiniteData.pages.flatMap((page) => {
      const items = page?.items;
      if (Array.isArray(items)) return items;
      return [];
    });

    return allAlerts;
  }, [widgetAlertsResponse]);

  const { alertFields, defaultValues, isLoadingAlert, isLoadingAlertFields } =
    useWidgetAlerts({ isEnabled: open && view === 'form' });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Record<string, any>>({
    defaultValues,
    mode: 'all',
  });

  // Watch criteria fields to control conditional field rendering
  const { fieldVisibility, compareType } = useAlertCriteriaWatch(control);

  // Reset view and form when drawer opens/closes
  useEffect(() => {
    if (open) {
      setView('list');
      setSelectedAlert(null);
    }
  }, [open]); // setView and setSelectedAlert are stable and don't need to be in deps

  // Reset form when editing an existing alert.
  // Depends on `isLoadingAlertFields` so it re-fires once fields finish loading and
  // Controllers are mounted — that is when the reset values actually take effect.
  useEffect(() => {
    if (view !== 'form' || !selectedAlert || isLoadingAlertFields) return;

    const ct = selectedAlert.compare_type;
    const thresholdVal = selectedAlert.criteria?.threshold;

    // Build an explicitly flat object — never spread `selectedAlert` directly
    // because that would place a nested `criteria` object into the RHF store,
    // causing Controller reads to be inconsistent.
    const formData: Record<string, any> = {
      // Top-level alert fields
      title: selectedAlert.title,
      compare_type: selectedAlert.compare_type,
      redirect_url: selectedAlert.redirect_url,
      domains: selectedAlert.domains,
      cadence: selectedAlert.cadence,
      cadence_time: selectedAlert.cadence_time,
      parameters: selectedAlert.parameters,
      // Criteria sub-fields hoisted to top level to match flatFieldName Controllers
      data_column: selectedAlert.criteria?.data_column,
      operator: selectedAlert.criteria?.operator,
      threshold_reference: selectedAlert.criteria?.threshold_reference,
      threshold_column: selectedAlert.criteria?.threshold_column ?? null,
      threshold_column_value: selectedAlert.criteria?.threshold_column_value ?? null,
      alert_expiration: selectedAlert.criteria?.alert_expiration ?? null,
    };

    if (thresholdVal !== undefined && thresholdVal !== null) {
      if (ct === 'relative_percent' || ct === 'percent_of_total') {
        formData.threshold_float = Number(thresholdVal) * 100;
      } else if (ct === 'relative_amount' || ct === 'static') {
        formData.threshold_int = Number(thresholdVal);
      } else {
        formData.threshold_text = String(thresholdVal);
      }
    }

    reset(formData);
  }, [view, selectedAlert, isLoadingAlertFields, reset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset form to defaults when creating a new alert.
  // Depends on `defaultValues` so it re-fires once alertFields finish loading.
  useEffect(() => {
    if (view !== 'form' || selectedAlert) return;
    reset(defaultValues);
  }, [view, selectedAlert, defaultValues, reset]);

  // Handlers for navigation
  const handleSelectAlert = (alert: TWidgetAlert) => {
    setSelectedAlert(alert);
    setView('form');
  };

  const handleCreateNew = () => {
    setSelectedAlert(null);
    setView('form');
  };

  const handleBackToList = () => {
    // Reset form and go back to list view.
    reset(defaultValues);
    setSelectedAlert(null);
    setView('list');
    queryClient.invalidateQueries({
      queryKey: ['getWidgetAlerts', 'widget-alerts-list', widget?.id, dashboard?.id],
    });
  };

  const handleCloseDrawer = () => {
    setView('list');
    setSelectedAlert(null);
    onClose();
  };

  // Create alert mutation
  const createAlertMutation = useMutation({
    mutationFn: async (payload: TPostWidgetAlertPayload) => {
      const extendedDataProvider = dataProvider as typeof dataProvider &
        DashboardsProviderType;
      // TODO: need a function to format the payload.
      return await extendedDataProvider.postWidgetAlert(payload);
    },
    onSuccess: () => {
      // Invalidate queries to refresh widget data (this automatically triggers refetch)
      queryClient.invalidateQueries({ queryKey: ['widget-alerts-list', widget?.id] });
      queryClient.invalidateQueries({ queryKey: ['widget-alert', widget?.id] });
      queryClient.invalidateQueries({ queryKey: ['widget-data', widget?.id] });
      notify(t('blox.dashboards.alerts.success.create') || 'Alert created successfully', {
        type: 'success',
      });
    },
    onError: (error) => {
      console.error('[WidgetAlerts] Create error:', error);
      notify(t('blox.error.oops') || 'Error creating alert', { type: 'error' });
    },
  });

  // Update alert mutation.
  const updateAlertMutation = useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: TPutWidgetAlertByIdPayload;
    }) => {
      const extendedDataProvider = dataProvider as typeof dataProvider &
        DashboardsProviderType;
      return await extendedDataProvider.putWidgetAlertById({ id, payload });
    },
    onSuccess: () => {
      // Invalidate queries to refresh widget data (this automatically triggers refetch)
      queryClient.invalidateQueries({ queryKey: ['widget-alerts-list', widget?.id] });
      queryClient.invalidateQueries({ queryKey: ['widget-alert', widget?.id] });
      queryClient.invalidateQueries({ queryKey: ['widget-data', widget?.id] });
      notify(t('blox.dashboards.alerts.success.update') || 'Alert updated successfully', {
        type: 'success',
      });
    },
    onError: (error) => {
      console.error('[WidgetAlerts] Update error:', error);
      notify(t('blox.error.oops') || 'Error updating alert', { type: 'error' });
    },
  });

  // Delete alert mutation.
  const deleteAlertMutation = useMutation({
    mutationFn: async ({ id }: TDeleteWidgetAlertByIdParams) => {
      const extendedDataProvider = dataProvider as typeof dataProvider &
        DashboardsProviderType;
      return await extendedDataProvider.deleteWidgetAlertById({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widget-alerts-list', widget?.id] });
      queryClient.invalidateQueries({ queryKey: ['widget-alert', widget?.id] });
      queryClient.invalidateQueries({ queryKey: ['widget-data', widget?.id] });
      queryClient.invalidateQueries({
        queryKey: ['getWidgetAlerts', 'widget-alerts-list', widget?.id, dashboard?.id],
      });
      notify(t('blox.dashboards.alerts.success.delete') || 'Alert deleted successfully', {
        type: 'success',
      });
    },
    onError: (error) => {
      console.error('[WidgetAlerts] Delete error:', error);
      notify(t('blox.error.oops') || 'Error deleting alert', { type: 'error' });
    },
    onSettled: () => {
      setDeletingAlertId(null);
    },
  });

  const handleDeleteAlert = async (alertId: string) => {
    setDeletingAlertId(alertId);
    await deleteAlertMutation.mutateAsync({ id: alertId });
  };

  const handleDeleteConfirmFromForm = async () => {
    if (!selectedAlert) return;
    setDeleteConfirmOpen(false);
    await handleDeleteAlert(selectedAlert.id);
    handleBackToList();
  };

  // Submit handler.
  // This needs to handle both create and update logic, as well as extract the correct threshold value based on which field is shown in the UI.
  const onSubmit: SubmitHandler<Record<string, any>> = async (formData) => {
    if (!widget?.id || !dashboard?.id) {
      notify(t('blox.error.oops') || 'Missing widget or dashboard ID', {
        type: 'error',
      });
      return;
    }

    // Extract threshold value from whichever child field was rendered based on compare_type.
    // threshold_float is stored as a display percentage (e.g., 15 for 15%) and must be
    // converted to a float before sending in the payload.
    const getThresholdValue = (): number | string => {
      const ct = formData.compare_type;

      if (ct === 'relative_percent' || ct === 'percent_of_total') {
        const displayVal = formData.threshold_float;
        if (displayVal !== undefined && displayVal !== null && displayVal !== '') {
          return Number(displayVal) / 100;
        }
      } else if (ct === 'relative_amount' || ct === 'static') {
        const val = formData.threshold_int;
        if (val !== undefined && val !== null && val !== '') {
          return Number(val);
        }
      } else {
        // Try threshold_text first, then fall back to any threshold_* key present
        const val = formData.threshold_text;
        if (val !== undefined && val !== null && val !== '') {
          return val;
        }
      }

      return 0; // fallback
    };

    // Prepare payload according to API schema.
    // formData is flat — all criteria values live at the top level.
    const payload: TPostWidgetAlertPayload = {
      title: formData.title,
      compare_type: formData.compare_type,
      widget_id: widget.id,
      dashboard_id: dashboard.id,
      redirect_url: formData.redirect_url,
      domains: formData.domains,
      cadence: formData.cadence,
      cadence_time: formData.cadence_time,
      criteria: {
        data_column: formData.data_column,
        operator: formData.operator,
        threshold: getThresholdValue() as any,
        threshold_reference: formData.threshold_reference,
        ...(formData.threshold_column && {
          threshold_column: formData.threshold_column,
        }),
        ...(formData.threshold_column_value && {
          threshold_column_value: formData.threshold_column_value,
        }),
        ...(formData.alert_expiration && {
          alert_expiration: formData.alert_expiration,
        }),
      },
      parameters: formData.parameters,
    };

    // If selectedAlert exists, update it; otherwise create new
    try {
      if (selectedAlert?.id) {
        await updateAlertMutation.mutateAsync({ id: selectedAlert.id, payload });
      } else {
        await createAlertMutation.mutateAsync(payload);
      }
      handleBackToList();
    } catch {
      // Error is handled by the mutation's onError callback
    }
  };

  const onError: SubmitErrorHandler<Record<string, any>> = (errors) => {
    notify(t('blox.error.oops') || 'Please fix the form errors', {
      type: 'error',
    });
  };

  const hasAlertFields = Boolean(alertFields?.length);

  // Compute form errors for display
  const formErrors = useMemo(() => {
    const errorList: Array<{ field: string; fieldLabel: string; message: string }> = [];

    // Helper to get user-friendly field label
    const getFieldLabel = (fieldName: string): string => {
      const field = alertFields.find((f) => f.name === fieldName);
      return field?.label || fieldName;
    };

    // Collect errors from react-hook-form
    Object.entries(errors).forEach(([fieldName, error]) => {
      if (error && typeof error === 'object' && 'message' in error) {
        errorList.push({
          field: fieldName,
          fieldLabel: getFieldLabel(fieldName),
          message: error.message as string,
        });
      }
    });

    // Add invalid fields tracked separately
    invalidFields.forEach((fieldName) => {
      // Avoid duplicates
      if (!errorList.some((e) => e.field === fieldName)) {
        errorList.push({
          field: fieldName,
          fieldLabel: getFieldLabel(fieldName),
          message: t('blox.error.field_invalid') || 'This field has an error',
        });
      }
    });

    return errorList;
  }, [errors, invalidFields, alertFields, t]);

  const hasFormErrors = formErrors.length > 0;

  // Compute title based on view
  const drawerTitle =
    view === 'list' ? (
      <Box
        data-testid="widget-alerts-drawer-title"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
      >
        <BetaFlag fontSize="medium" />
        {t('blox.dashboards.alerts.manage_alerts', { title: widget?.title })}
      </Box>
    ) : (
      <Box
        data-testid="widget-alerts-drawer-title"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
      >
        <IconButton
          data-testid="widget-alerts-back-button"
          size="small"
          onClick={handleBackToList}
          disabled={
            isLoadingAlert ||
            isLoadingAlertFields ||
            createAlertMutation.isPending ||
            updateAlertMutation.isPending
          }
          sx={{ mr: 0.5 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <BetaFlag fontSize="medium" />
        {selectedAlert
          ? t('blox.dashboards.alerts.edit_alert', { title: widget?.title })
          : t('blox.dashboards.alerts.create_alert', { title: widget?.title })}
      </Box>
    );

  // Compute actions based on view
  const drawerActions =
    view === 'list' ? (
      <Button
        onClick={handleCloseDrawer}
        disableElevation
      >
        {t('blox.actions.close')}
      </Button>
    ) : (
      <Box
        data-testid="widget-alerts-actions"
        sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'space-between' }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedAlert && (
            <Button
              data-testid="widget-alerts-delete-button"
              variant="outlined"
              color="error"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={
                isLoadingAlert ||
                isLoadingAlertFields ||
                createAlertMutation.isPending ||
                updateAlertMutation.isPending ||
                deleteAlertMutation.isPending
              }
              disableElevation
            >
              {t('blox.actions.delete') || 'Delete'}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            data-testid="widget-alerts-cancel-button"
            onClick={handleBackToList}
            disabled={
              isLoadingAlert ||
              isLoadingAlertFields ||
              createAlertMutation.isPending ||
              updateAlertMutation.isPending
            }
            disableElevation
          >
            {t('blox.actions.cancel')}
          </Button>
          <Button
            data-testid="widget-alerts-save-button"
            variant="contained"
            onClick={handleSubmit(onSubmit, onError)}
            disabled={
              !hasAlertFields ||
              isLoadingAlert ||
              isLoadingAlertFields ||
              createAlertMutation.isPending ||
              updateAlertMutation.isPending ||
              invalidFields.size > 0 ||
              Object.keys(errors).length > 0
            }
            disableElevation
          >
            {selectedAlert
              ? t('blox.actions.update') || 'Update Alert'
              : t('blox.actions.save') || 'Save Alert'}
          </Button>
        </Box>
      </Box>
    );

  return (
    <>
      <DrawerWrapper
        data-testid="widget-alerts-drawer"
        open={open}
        onClose={handleCloseDrawer}
        title={drawerTitle}
        actions={drawerActions}
        loading={
          view === 'list'
            ? isLoadingWidgetAlerts
            : isLoadingAlert ||
              isLoadingAlertFields ||
              createAlertMutation.isPending ||
              updateAlertMutation.isPending
        }
        sx={{
          // Asset Dashboard: ensure the drawer renders above the AssetActionBar and still renders nested dynamic fields.
          zIndex: 1300,
        }}
      >
        {view === 'list' ? (
          <WidgetAlertsList
            alerts={widgetAlerts}
            isLoading={isLoadingWidgetAlerts}
            isFetching={isFetchingWidgetAlerts}
            onSelectAlert={handleSelectAlert}
            onCreateNew={handleCreateNew}
            onDeleteAlert={handleDeleteAlert}
            deletingAlertId={deletingAlertId}
            hasNextPage={hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
            intersectionRef={intersectionRef}
            intersectionEntry={entry}
          />
        ) : (
          <Box
            data-testid={`widget-alerts-form-container--${widget?.id}`}
            sx={{ position: 'relative' }}
          >
            <Box
              data-testid={`widget-alerts-form--${widget?.id}`}
              component="form"
              onSubmit={handleSubmit(onSubmit, onError)}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mx: 1, mt: 3 }}
            >
              {/* Form Errors Summary */}
              {hasFormErrors && (
                <Alert
                  data-testid="widget-alerts-form-errors"
                  severity="error"
                  sx={{ mb: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    {t('blox.error.form_has_errors') ||
                      'Please fix the following errors before saving:'}
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ m: 0, pl: 2 }}
                  >
                    {formErrors.map((error, index) => (
                      <Box
                        component="li"
                        key={`${error.field}-${index}`}
                        sx={{ mb: 0.5 }}
                      >
                        <Typography variant="body2">
                          <strong>{error.fieldLabel}:</strong> {error.message}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Alert>
              )}

              {/* Render dynamic alert fields grouped by category */}
              {hasAlertFields ? (
                FIELD_GROUPS.map((group) => {
                  // Get fields for this group
                  const groupFields = alertFields.filter((field) =>
                    group.fieldNames.includes(field.name),
                  );

                  // Skip empty groups
                  if (groupFields.length === 0) return null;

                  // Special handling for Alert Criteria section
                  if (group.key === 'criteria') {
                    // Extract specific fields for custom layout
                    const dataColumnField = groupFields.find(
                      (f) =>
                        f.name === 'data_column' ||
                        f.name === 'criteria.data_column' ||
                        f.name === 'column',
                    );
                    const thresholdReferenceField = groupFields.find(
                      (f) =>
                        f.name === 'threshold_reference' ||
                        f.name === 'criteria.threshold_reference',
                    );
                    const operatorField = groupFields.find(
                      (f) => f.name === 'operator' || f.name === 'criteria.operator',
                    );
                    const thresholdGroupField = groupFields.find(
                      (f) => f.name === 'threshold' && f.type === 'group',
                    );
                    const thresholdChildren =
                      (thresholdGroupField?.config?.children as DynamicFieldType[]) ?? [];

                    // Determine which threshold child to render based on compare_type:
                    //   relative_percent / percent_of_total → threshold_float (displayed as %)
                    //   relative_amount / static             → threshold_int
                    //   fallback                             → threshold_text
                    let activeThresholdChild: DynamicFieldType | null = null;
                    if (thresholdChildren.length > 0) {
                      if (
                        compareType === 'relative_percent' ||
                        compareType === 'percent_of_total'
                      ) {
                        activeThresholdChild =
                          thresholdChildren.find(
                            (c) => flatFieldName(c.name) === 'threshold_float',
                          ) ?? null;
                      } else if (
                        compareType === 'relative_amount' ||
                        compareType === 'static'
                      ) {
                        activeThresholdChild =
                          thresholdChildren.find(
                            (c) => flatFieldName(c.name) === 'threshold_int',
                          ) ?? null;
                      } else {
                        activeThresholdChild =
                          thresholdChildren.find(
                            (c) => flatFieldName(c.name) === 'threshold_text',
                          ) ??
                          thresholdChildren[0] ??
                          null;
                      }
                    }

                    const isPercentageThreshold =
                      flatFieldName(activeThresholdChild?.name ?? '') ===
                      'threshold_float';

                    const compareTypeField = groupFields.find(
                      (f) => f.name === 'compare_type',
                    );

                    // Hide the compare_type selector when there is only one option and it is "static"
                    const compareTypeOptions = compareTypeField?.config?.options ?? [];
                    const hideCompareType =
                      compareTypeOptions.length === 1 &&
                      compareTypeOptions[0].value === 'static';
                    const thresholdColumnField = groupFields.find(
                      (f) =>
                        f.name === 'threshold_column' ||
                        f.name === 'criteria.threshold_column',
                    );
                    const thresholdColumnValueField = groupFields.find(
                      (f) =>
                        f.name === 'threshold_column_value' ||
                        f.name === 'criteria.threshold_column_value',
                    );
                    const alertExpirationField = groupFields.find(
                      (f) =>
                        f.name === 'alert_expiration' ||
                        f.name === 'criteria.alert_expiration',
                    );

                    return (
                      <Paper
                        key={group.key}
                        data-testid={`widget-alerts-group--${group.key}`}
                        elevation={0}
                        sx={{
                          p: 2,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            fontSize: '1rem',
                            color: 'text.primary',
                          }}
                        >
                          {group.title}
                        </Typography>
                        <Box
                          data-testid={`widget-alerts-group-fields--${group.key}`}
                          sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
                        >
                          {/* Inline sentence: "When the [compare_type] of [data_column] is:" */}
                          {dataColumnField && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography
                                data-testid={`prefix-for-data-column--${dataColumnField.name}`}
                                variant="body2"
                                component="span"
                                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                              >
                                {t('blox.dashboards.alerts.form.when_the')}
                              </Typography>

                              {/* Comparison type inline */}
                              {compareTypeField && !hideCompareType && (
                                <>
                                  <Box
                                    data-testid={`widget-alerts-field--${compareTypeField.name}`}
                                    sx={{ flex: '1 1 160px', maxWidth: 280 }}
                                  >
                                    <Controller
                                      name={compareTypeField.name}
                                      control={control}
                                      rules={getAlertValidationRules({
                                        field: compareTypeField,
                                        t,
                                      })}
                                      render={({
                                        field: controllerField,
                                        fieldState,
                                      }) => (
                                        <DynamicFieldWrapper
                                          child={{
                                            ...compareTypeField,
                                            config: {
                                              ...compareTypeField.config,
                                            },
                                          }}
                                          value={controllerField?.value}
                                          onChange={(name: string, value: any) => {
                                            controllerField.onChange(value);
                                          }}
                                          isDisabled={
                                            isLoadingAlert ||
                                            isLoadingAlertFields ||
                                            createAlertMutation.isPending ||
                                            updateAlertMutation.isPending
                                          }
                                          error={fieldState.error?.message}
                                          onValidationChange={(
                                            fieldName: string,
                                            isValid: boolean,
                                          ) => {
                                            setInvalidFields((prev) => {
                                              const newSet = new Set(prev);
                                              if (isValid) {
                                                newSet.delete(fieldName);
                                              } else {
                                                newSet.add(fieldName);
                                              }
                                              return newSet;
                                            });
                                          }}
                                        />
                                      )}
                                    />
                                  </Box>
                                  <Typography
                                    data-testid={`of-for-data-column--${dataColumnField.name}`}
                                    variant="body2"
                                    component="span"
                                    sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                                  >
                                    {t('blox.dashboards.alerts.form.of')}
                                  </Typography>
                                </>
                              )}

                              {/* Threshold Calculation inline */}
                              {thresholdReferenceField && (
                                <Box
                                  sx={{ flex: '1 1 160px', maxWidth: 280 }}
                                  data-testid={`widget-alerts-field--${thresholdReferenceField.name}`}
                                >
                                  <Controller
                                    name={flatFieldName(thresholdReferenceField.name)}
                                    control={control}
                                    rules={getAlertValidationRules({
                                      field: thresholdReferenceField,
                                      t,
                                    })}
                                    render={({ field: controllerField, fieldState }) => (
                                      <DynamicFieldWrapper
                                        child={{
                                          ...thresholdReferenceField,
                                          config: {
                                            ...thresholdReferenceField.config,
                                          },
                                        }}
                                        value={controllerField?.value}
                                        onChange={(name: string, value: any) => {
                                          controllerField.onChange(value);
                                        }}
                                        isDisabled={
                                          isLoadingAlert ||
                                          isLoadingAlertFields ||
                                          createAlertMutation.isPending ||
                                          updateAlertMutation.isPending
                                        }
                                        error={fieldState.error?.message}
                                        onValidationChange={(
                                          fieldName: string,
                                          isValid: boolean,
                                        ) => {
                                          setInvalidFields((prev) => {
                                            const newSet = new Set(prev);
                                            if (isValid) {
                                              newSet.delete(fieldName);
                                            } else {
                                              newSet.add(fieldName);
                                            }
                                            return newSet;
                                          });
                                        }}
                                      />
                                    )}
                                  />
                                </Box>
                              )}

                              <Box
                                data-testid={`widget-alerts-field--${dataColumnField.name}`}
                                sx={{ flex: '1 1 160px', maxWidth: 280 }}
                              >
                                <Controller
                                  name={flatFieldName(dataColumnField.name)}
                                  control={control}
                                  rules={getAlertValidationRules({
                                    field: dataColumnField,
                                    t,
                                  })}
                                  render={({ field: controllerField, fieldState }) => (
                                    <DynamicFieldWrapper
                                      child={{
                                        ...dataColumnField,
                                        config: {
                                          ...dataColumnField.config,
                                          // hideLabel: true,
                                        },
                                      }}
                                      value={controllerField?.value}
                                      onChange={(name: string, value: any) => {
                                        controllerField.onChange(value);
                                      }}
                                      isDisabled={
                                        isLoadingAlert ||
                                        isLoadingAlertFields ||
                                        createAlertMutation.isPending ||
                                        updateAlertMutation.isPending
                                      }
                                      error={fieldState.error?.message}
                                      onValidationChange={(
                                        fieldName: string,
                                        isValid: boolean,
                                      ) => {
                                        setInvalidFields((prev) => {
                                          const newSet = new Set(prev);
                                          if (isValid) {
                                            newSet.delete(fieldName);
                                          } else {
                                            newSet.add(fieldName);
                                          }
                                          return newSet;
                                        });
                                      }}
                                    />
                                  )}
                                />
                              </Box>

                              <Typography
                                data-testid={`suffix-for-data-column--${dataColumnField.name}`}
                                variant="body2"
                                component="span"
                                sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}
                              >
                                {t('blox.dashboards.alerts.form.when_value_of_suffix')}
                              </Typography>
                            </Box>
                          )}

                          {/* Operator and Threshold on same line */}
                          {(operatorField || activeThresholdChild) && (
                            <Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 2,
                                  alignItems: 'flex-start',
                                }}
                              >
                                {/* Operator Field */}
                                {operatorField && (
                                  <Box
                                    sx={{ flex: '0 0 200px' }}
                                    data-testid={`widget-alerts-field--${operatorField.name}`}
                                  >
                                    <Controller
                                      name={flatFieldName(operatorField.name)}
                                      control={control}
                                      rules={getAlertValidationRules({
                                        field: operatorField,
                                        t,
                                      })}
                                      render={({
                                        field: controllerField,
                                        fieldState,
                                      }) => (
                                        <DynamicFieldWrapper
                                          child={operatorField}
                                          value={controllerField?.value}
                                          onChange={(name: string, value: any) => {
                                            controllerField.onChange(value);
                                          }}
                                          isDisabled={
                                            isLoadingAlert ||
                                            isLoadingAlertFields ||
                                            createAlertMutation.isPending ||
                                            updateAlertMutation.isPending
                                          }
                                          error={fieldState.error?.message}
                                          onValidationChange={(
                                            fieldName: string,
                                            isValid: boolean,
                                          ) => {
                                            setInvalidFields((prev) => {
                                              const newSet = new Set(prev);
                                              if (isValid) {
                                                newSet.delete(fieldName);
                                              } else {
                                                newSet.add(fieldName);
                                              }
                                              return newSet;
                                            });
                                          }}
                                        />
                                      )}
                                    />
                                  </Box>
                                )}

                                {/* Threshold Value Field - active child determined by compare_type */}
                                {activeThresholdChild && (
                                  <Box sx={{ flex: 1 }}>
                                    <Box
                                      data-testid={`widget-alerts-field--${activeThresholdChild.name}`}
                                    >
                                      {isPercentageThreshold ? (
                                        // threshold_float: user enters a percentage value (e.g. 15 for 15%).
                                        // The display value is stored as-is in the form; getThresholdValue()
                                        // divides by 100 before putting it in the payload.
                                        <Controller
                                          name="threshold_float"
                                          control={control}
                                          rules={{
                                            required:
                                              t('blox.error.required') ||
                                              'This field is required',
                                            min: {
                                              value: 0,
                                              message:
                                                t('blox.error.min_value', { min: 0 }) ||
                                                'Must be greater than 0',
                                            },
                                          }}
                                          render={({
                                            field: controllerField,
                                            fieldState,
                                          }) => (
                                            <TextField
                                              label={`${activeThresholdChild.label} (%)`}
                                              type="number"
                                              fullWidth
                                              variant="outlined"
                                              value={controllerField.value ?? ''}
                                              onChange={(e) =>
                                                controllerField.onChange(
                                                  e.target.value === ''
                                                    ? ''
                                                    : Number(e.target.value),
                                                )
                                              }
                                              disabled={
                                                !compareType ||
                                                isLoadingAlert ||
                                                isLoadingAlertFields ||
                                                createAlertMutation.isPending ||
                                                updateAlertMutation.isPending
                                              }
                                              error={!!fieldState.error}
                                              helperText={fieldState.error?.message}
                                              slotProps={{
                                                input: {
                                                  endAdornment: (
                                                    <InputAdornment position="end">
                                                      %
                                                    </InputAdornment>
                                                  ),
                                                },

                                                htmlInput: { min: 0, step: 1 },

                                                inputLabel: {
                                                  shrink: true,
                                                  sx: { zIndex: 0 },
                                                },
                                              }}
                                            />
                                          )}
                                        />
                                      ) : (
                                        <Controller
                                          name={flatFieldName(activeThresholdChild.name)}
                                          control={control}
                                          rules={getAlertValidationRules({
                                            field: activeThresholdChild,
                                            t,
                                          })}
                                          render={({
                                            field: controllerField,
                                            fieldState,
                                          }) => (
                                            <DynamicFieldWrapper
                                              child={activeThresholdChild}
                                              value={controllerField?.value}
                                              onChange={(name: string, value: any) => {
                                                controllerField.onChange(value);
                                              }}
                                              isDisabled={
                                                !compareType ||
                                                isLoadingAlert ||
                                                isLoadingAlertFields ||
                                                createAlertMutation.isPending ||
                                                updateAlertMutation.isPending
                                              }
                                              error={fieldState.error?.message}
                                              onValidationChange={(
                                                fieldName: string,
                                                isValid: boolean,
                                              ) => {
                                                setInvalidFields((prev) => {
                                                  const newSet = new Set(prev);
                                                  if (isValid) {
                                                    newSet.delete(fieldName);
                                                  } else {
                                                    newSet.add(fieldName);
                                                  }
                                                  return newSet;
                                                });
                                              }}
                                            />
                                          )}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          )}

                          {/* Conditional threshold_column field */}
                          {thresholdColumnField && (
                            <Collapse
                              in={fieldVisibility.shouldShowThresholdColumn}
                              timeout={300}
                              unmountOnExit
                            >
                              <Paper
                                variant="outlined"
                                sx={{ p: 2, borderRadius: 1 }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: 600,
                                    color: 'text.secondary',
                                    display: 'block',
                                    mb: 1.5,
                                  }}
                                >
                                  Threshold
                                </Typography>
                                <Box
                                  data-testid={`widget-alerts-field--${thresholdColumnField.name}`}
                                >
                                  <Controller
                                    name={flatFieldName(thresholdColumnField.name)}
                                    control={control}
                                    rules={getAlertValidationRules({
                                      field: thresholdColumnField,
                                      t,
                                    })}
                                    render={({ field: controllerField, fieldState }) => (
                                      <DynamicFieldWrapper
                                        child={thresholdColumnField}
                                        value={controllerField?.value}
                                        onChange={(name: string, value: any) => {
                                          controllerField.onChange(value);
                                        }}
                                        isDisabled={
                                          isLoadingAlert ||
                                          isLoadingAlertFields ||
                                          createAlertMutation.isPending ||
                                          updateAlertMutation.isPending
                                        }
                                        error={fieldState.error?.message}
                                        onValidationChange={(
                                          fieldName: string,
                                          isValid: boolean,
                                        ) => {
                                          setInvalidFields((prev) => {
                                            const newSet = new Set(prev);
                                            if (isValid) {
                                              newSet.delete(fieldName);
                                            } else {
                                              newSet.add(fieldName);
                                            }
                                            return newSet;
                                          });
                                        }}
                                      />
                                    )}
                                  />
                                </Box>
                              </Paper>
                            </Collapse>
                          )}

                          {/* Conditional threshold_column_value field */}
                          {thresholdColumnValueField && (
                            <Collapse
                              in={fieldVisibility.shouldShowThresholdColumnValue}
                              timeout={300}
                              unmountOnExit
                            >
                              <Box
                                data-testid={`widget-alerts-field--${thresholdColumnValueField.name}`}
                              >
                                <Controller
                                  name={flatFieldName(thresholdColumnValueField.name)}
                                  control={control}
                                  rules={getAlertValidationRules({
                                    field: thresholdColumnValueField,
                                    t,
                                  })}
                                  render={({ field: controllerField, fieldState }) => (
                                    <DynamicFieldWrapper
                                      child={thresholdColumnValueField}
                                      value={controllerField?.value}
                                      onChange={(name: string, value: any) => {
                                        controllerField.onChange(value);
                                      }}
                                      isDisabled={
                                        isLoadingAlert ||
                                        isLoadingAlertFields ||
                                        createAlertMutation.isPending ||
                                        updateAlertMutation.isPending
                                      }
                                      error={fieldState.error?.message}
                                      onValidationChange={(
                                        fieldName: string,
                                        isValid: boolean,
                                      ) => {
                                        setInvalidFields((prev) => {
                                          const newSet = new Set(prev);
                                          if (isValid) {
                                            newSet.delete(fieldName);
                                          } else {
                                            newSet.add(fieldName);
                                          }
                                          return newSet;
                                        });
                                      }}
                                    />
                                  )}
                                />
                              </Box>
                            </Collapse>
                          )}

                          {/* Alert Expiration */}
                          {alertExpirationField && (
                            <Box
                              data-testid={`widget-alerts-field--${alertExpirationField.name}`}
                            >
                              <Controller
                                name={flatFieldName(alertExpirationField.name)}
                                control={control}
                                rules={getAlertValidationRules({
                                  field: alertExpirationField,
                                  t,
                                })}
                                render={({ field: controllerField, fieldState }) => (
                                  <DynamicFieldWrapper
                                    child={alertExpirationField}
                                    value={controllerField?.value}
                                    onChange={(name: string, value: any) => {
                                      controllerField.onChange(value);
                                    }}
                                    isDisabled={
                                      isLoadingAlert ||
                                      isLoadingAlertFields ||
                                      createAlertMutation.isPending ||
                                      updateAlertMutation.isPending
                                    }
                                    error={fieldState.error?.message}
                                    onValidationChange={(
                                      fieldName: string,
                                      isValid: boolean,
                                    ) => {
                                      setInvalidFields((prev) => {
                                        const newSet = new Set(prev);
                                        if (isValid) {
                                          newSet.delete(fieldName);
                                        } else {
                                          newSet.add(fieldName);
                                        }
                                        return newSet;
                                      });
                                    }}
                                  />
                                )}
                              />
                            </Box>
                          )}

                          {/* Generic rendering for remaining criteria fields not handled above */}
                          {groupFields
                            .filter((f) => {
                              const speciallyHandledNames = [
                                compareTypeField?.name,
                                dataColumnField?.name,
                                operatorField?.name,
                                thresholdReferenceField?.name,
                                thresholdGroupField?.name,
                                thresholdColumnField?.name,
                                thresholdColumnValueField?.name,
                                alertExpirationField?.name,
                              ].filter(Boolean);
                              return !speciallyHandledNames.includes(f.name);
                            })
                            .map((f) => (
                              <Box
                                key={f.name}
                                data-testid={`widget-alerts-field--${f.name}`}
                              >
                                <Controller
                                  name={flatFieldName(f.name)}
                                  control={control}
                                  rules={getAlertValidationRules({ field: f, t })}
                                  render={({ field: controllerField, fieldState }) => (
                                    <DynamicFieldWrapper
                                      child={f}
                                      value={controllerField?.value}
                                      onChange={(_name: string, value: any) => {
                                        controllerField.onChange(value);
                                      }}
                                      isDisabled={
                                        isLoadingAlert ||
                                        isLoadingAlertFields ||
                                        createAlertMutation.isPending ||
                                        updateAlertMutation.isPending
                                      }
                                      error={fieldState.error?.message}
                                      onValidationChange={(
                                        fieldName: string,
                                        isValid: boolean,
                                      ) => {
                                        setInvalidFields((prev) => {
                                          const newSet = new Set(prev);
                                          if (isValid) {
                                            newSet.delete(fieldName);
                                          } else {
                                            newSet.add(fieldName);
                                          }
                                          return newSet;
                                        });
                                      }}
                                    />
                                  )}
                                />
                              </Box>
                            ))}
                        </Box>
                      </Paper>
                    );
                  }

                  // Default rendering for other groups
                  return (
                    <Paper
                      key={group.key}
                      elevation={0}
                      data-testid={`widget-alerts-group--${group.key}`}
                      sx={{
                        p: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      {/* Group Header - more compact styling */}
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: '1rem',
                          color: 'text.primary',
                        }}
                      >
                        {group.title}
                      </Typography>

                      {/* Group Fields */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        {groupFields.map((dynamicFieldConfig: DynamicFieldType) => {
                          const fieldName = dynamicFieldConfig.name;

                          // Determine if this field should be conditionally shown
                          const isConditionalThresholdColumn =
                            fieldName === 'threshold_column' ||
                            fieldName === 'criteria.threshold_column';
                          const isConditionalThresholdColumnValue =
                            fieldName === 'threshold_column_value' ||
                            fieldName === 'criteria.threshold_column_value';

                          const shouldShow =
                            (!isConditionalThresholdColumn &&
                              !isConditionalThresholdColumnValue) ||
                            (isConditionalThresholdColumn &&
                              fieldVisibility.shouldShowThresholdColumn) ||
                            (isConditionalThresholdColumnValue &&
                              fieldVisibility.shouldShowThresholdColumnValue);

                          return (
                            <Collapse
                              key={dynamicFieldConfig.name}
                              in={shouldShow}
                              timeout={300}
                              unmountOnExit
                            >
                              <Box
                                data-testid={`widget-alerts-field--${dynamicFieldConfig.name}`}
                              >
                                <Controller
                                  name={dynamicFieldConfig.name}
                                  control={control}
                                  rules={getAlertValidationRules({
                                    field: dynamicFieldConfig,
                                    t,
                                  })}
                                  render={({ field: controllerField, fieldState }) => (
                                    <DynamicFieldWrapper
                                      child={dynamicFieldConfig}
                                      value={controllerField?.value}
                                      onChange={(name: string, value: any) => {
                                        controllerField.onChange(value);
                                      }}
                                      isDisabled={
                                        isLoadingAlert ||
                                        isLoadingAlertFields ||
                                        createAlertMutation.isPending ||
                                        updateAlertMutation.isPending
                                      }
                                      error={fieldState.error?.message}
                                      onValidationChange={(
                                        fieldName: string,
                                        isValid: boolean,
                                      ) => {
                                        setInvalidFields((prev) => {
                                          const newSet = new Set(prev);
                                          if (isValid) {
                                            newSet.delete(fieldName);
                                          } else {
                                            newSet.add(fieldName);
                                          }
                                          return newSet;
                                        });
                                      }}
                                    />
                                  )}
                                />
                              </Box>
                            </Collapse>
                          );
                        })}
                      </Box>
                    </Paper>
                  );
                })
              ) : (
                <Alert
                  data-testid="widget-alerts-no-fields-alert"
                  severity="info"
                  variant="outlined"
                >
                  {t('blox.dashboards.alerts.errors.no_fields') ||
                    'No alert configuration available for this widget.'}
                </Alert>
              )}
            </Box>
          </Box>
        )}
      </DrawerWrapper>
      <DragModal
        data-testid="widget-alert-delete-confirm-modal"
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        isDraggable
        isResizable={false}
        initialWidth="400px"
        initialHeight="auto"
        header={
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600 }}
          >
            {t('blox.dashboards.alerts.delete.title') || 'Delete alert?'}
          </Typography>
        }
        body={
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('blox.dashboards.alerts.delete.warning') ||
              'This action cannot be undone.'}
          </Typography>
        }
        footer={
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}
          >
            <Button
              data-testid="widget-alert-delete-cancel-button--form"
              size="small"
              onClick={() => setDeleteConfirmOpen(false)}
              disableElevation
            >
              {t('blox.actions.cancel') || 'Cancel'}
            </Button>
            <Button
              data-testid="widget-alert-delete-confirm-button--form"
              size="small"
              variant="contained"
              color="error"
              onClick={handleDeleteConfirmFromForm}
              disabled={deleteAlertMutation.isPending}
              disableElevation
            >
              {t('blox.actions.delete') || 'Delete'}
            </Button>
          </Box>
        }
      />
    </>
  );
};

export default WidgetAlerts;
